import React, { useEffect, useState, ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Box, Typography, Button } from '@mui/material';
import { Machine, get_preferred_connection } from './ts/Connections';
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useTranslation } from 'react-i18next';

const MY_HOST = import.meta.env.VITE_MY_HOST || "https://spectre-licensing.com";

interface MachinePingWrapperProps {
    machine: Machine | false;
    setSelectedMachine: (machine: Machine) => void;
    cookies: {
        user64: string;
        [key: string]: any;
    };
    children: ReactNode;
}

export default function MachinePingWrapper({
    children,
    machine,
    setSelectedMachine,
    cookies
}: MachinePingWrapperProps): JSX.Element {
    const [loading, setLoading] = useState<boolean>(!machine);
    const [error, setError] = useState<string | null>(null);
    const { id: machineName } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const appInsights = useAppInsightsContext();
    const { t } = useTranslation();

    useEffect(() => {
        // If machine is already set, we don't need to do anything
        if (machine) {
            setLoading(false);
            return;
        }

        // Try to connect to the machine specified in the URL
        const connectToMachine = async () => {
            if (!machineName) {
                setError("Machine name not found in URL");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                // Fetch machine list
                const response = await fetch(`${MY_HOST}/api/matchmaking/list`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch machine list');
                }

                const data = await response.json();

                if (!data.matches || data.matches.length === 0) {
                    throw new Error('No machines available');
                }

                // Find the machine that matches the URL
                const machineData = data.matches.find(
                    (m: any) => m.machineName === machineName
                );

                if (!machineData) {
                    throw new Error(`Machine "${machineName}" not found, machine may have been renamed`);
                }

                // Try to establish a connection
                const connection = await get_preferred_connection(machineData, cookies.user64);

                if (!connection) {
                    throw new Error('Failed to establish connection');
                }

                // Create a new Machine instance
                const newMachine = new Machine(
                    connection,
                    machineData.machineName,
                    machineData.localIP,
                    machineData.publicIP,
                    machineData.matchmakingID,
                    machineData.location,
                    machineData.macAddress,
                    machineData.scanMode,
                    machineData.certification,
                    machineData.version,
                    machineData.serialNumber
                );

                // Test the connection with a ping
                const pingSuccess = await newMachine.connection.ping(newMachine);

                if (!pingSuccess) {
                    throw new Error('Machine is not responding to ping');
                }

                // Successfully connected, update the parent component
                setSelectedMachine(newMachine);
                setLoading(false);
            } catch (err) {
                console.error('Error connecting to machine:', err);
                const errorMessage = err instanceof Error ? err.message : 'Failed to connect to machine';

                // Track the exception with AppInsights
                appInsights.trackException({
                    exception: err instanceof Error ? err : new Error(errorMessage),
                    severityLevel: 3, // Error
                    properties: {
                        component: "MachinePingWrapper",
                        action: "connectToMachine",
                        machineName: machineName,
                        user: cookies.user64
                    }
                });

                setError(errorMessage);
                setLoading(false);
            }
        };

        connectToMachine();
    }, [machine, machineName, cookies.user64, setSelectedMachine, appInsights]);

    const styles = {
        buttonText: {
            fontSize: 15,
            fontWeight: 600,
        },
    }

    // Show loading state
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'calc(100vh - 64px)',
                    mt: 8
                }}
            >
                <CircularProgress size={64} sx={{ color: "#F48430" }} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    {t('machinePingWrapper.connectingTo', { machineName })}
                </Typography>
            </Box>
        );
    }

    // Show error state with retry button
    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'calc(100vh - 64px)',
                    mt: 8
                }}
            >
                <Typography variant="h5" color="error" display="inline" sx={{ fontWeight: 500, }} gutterBottom>{t('machinePingWrapper.connectionError')}</Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    {error}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        className="Nav-Bar-Button"
                        variant="contained"
                        onClick={() => navigate('/matchmaking')}
                    >
                        <Typography sx={styles.buttonText}>{t('machinePingWrapper.mySpectres')}</Typography>
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => window.location.reload()}
                        sx={{ borderColor: '#262D6D', color: '#262D6D' }}
                    >
                        <Typography sx={styles.buttonText}>{t('machinePingWrapper.retryConnection')}</Typography>
                    </Button>
                </Box>
            </Box>
        );
    }

    // If everything is fine, render the children
    return <>{children}</>;
}